import { useEffect } from 'react';

// ═══════════════════════════════════════════════════════════════
// ESC billentyű: a legfelső nyitott ablak (modal) bezárása – az egész alkalmazásban
//
// Nem kell minden ablakba külön kezelőt írni: a legfelső teljes képernyős réteg
// (`fixed inset-0`) bezáró gombját nyomja meg – ugyanazt, amit a felhasználó is
// megnyomna, így az ablak saját bezárási logikája fut le (megerősítés, takarítás).
//
// Bezáró gomb (ebben a sorrendben):
//   1. [data-modal-close]
//   2. aria-label / title: „Bezár…”, „Close”
//   3. csak X ikont tartalmazó gomb
//   4. „Mégse” / „Mégsem” / „Bezárás” feliratú gomb
// Nem zár be:
//   - [data-no-esc] jelölésű réteget (pl. karbantartási képernyő),
//   - [data-esc-self] jelölésű réteget (az ablak saját ESC-kezelővel rendelkezik),
//   - ha egy ablak maga már kezelte az ESC-et (event.defaultPrevented),
//   - folyamatban lévő szövegbevitel-összeállítás (IME) közben,
//   - ha a rétegben nincs felismerhető bezáró gomb (ilyenkor semmit sem csinál).
// ═══════════════════════════════════════════════════════════════

const LABEL_RE = /bezár|close/i;
const TEXT_RE = /^(mégse|mégsem|bezárás|bezár)$/i;

function isVisible(el: HTMLElement): boolean {
  if (!el.isConnected) return false;
  const r = el.getBoundingClientRect();
  if (r.width === 0 || r.height === 0) return false;
  const cs = getComputedStyle(el);
  return cs.visibility !== 'hidden' && cs.display !== 'none' && cs.pointerEvents !== 'none';
}

function zIndexOf(el: HTMLElement): number {
  const z = parseInt(getComputedStyle(el).zIndex, 10);
  return Number.isFinite(z) ? z : 0;
}

function isXIcon(svg: Element): boolean {
  const cls = svg.getAttribute('class') || '';
  return /(^|\s)lucide-(x|circle-x|x-circle)(-icon)?(\s|$)/.test(cls);
}

export function findCloseButton(layer: HTMLElement): HTMLButtonElement | null {
  const buttons = Array.from(layer.querySelectorAll<HTMLButtonElement>('button, [role="button"]'))
    .filter((b) => !(b as HTMLButtonElement).disabled && isVisible(b));

  const explicit = buttons.find((b) => b.hasAttribute('data-modal-close'));
  if (explicit) return explicit;

  const labelled = buttons.find((b) => LABEL_RE.test(b.getAttribute('aria-label') || '') || LABEL_RE.test(b.getAttribute('title') || ''));
  if (labelled) return labelled;

  const xOnly = buttons.find((b) => {
    if ((b.textContent || '').trim()) return false;
    const svgs = b.querySelectorAll('svg');
    return svgs.length === 1 && isXIcon(svgs[0]);
  });
  if (xOnly) return xOnly;

  return buttons.find((b) => TEXT_RE.test((b.textContent || '').trim())) || null;
}

/** A nyitott rétegek, legfelső elöl (z-index, majd DOM-sorrend szerint). */
export function openLayers(): HTMLElement[] {
  const all = Array.from(document.querySelectorAll<HTMLElement>('.fixed.inset-0'))
    .filter((el) => isVisible(el));
  return all
    .map((el, i) => ({ el, i, z: zIndexOf(el) }))
    .sort((a, b) => b.z - a.z || b.i - a.i)
    .map((x) => x.el);
}

export function useGlobalEscapeClose(): void {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || e.defaultPrevented || e.isComposing) return;
      for (const layer of openLayers()) {
        if (layer.closest('[data-no-esc]')) return; // nem zárható réteg van legfelül
        if (layer.closest('[data-esc-self]')) return; // az ablak maga kezeli az ESC-et
        const btn = findCloseButton(layer);
        if (btn) {
          e.preventDefault();
          btn.click();
          return;
        }
        // Gomb nélküli réteg (pl. háttér-elsötétítés) → a következő réteggel próbálkozik
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
}
